import React from 'react';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import { connect } from 'react-redux';
import {
  Icon,
  Typography,
  Button,
} from '@mui/material';
import { DynamicTable, TitleTooltip } from 'components';
import { withRouter} from 'utils';
import { DateUtil } from 'doctivity-shared/utils';
import { queryCustomServiceLines, listClaimsServicelines, } from 'store/actions/claimsActions';
import { CustomServiceLineDialog } from 'components';
import moment from 'moment';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
  },
  spacer: {
    flex: 1,
  },
  addButton: {
    textTransform: 'none',
  },
});

const columns = [
  {
    label: 'Name',
    key: 'name',
    style: { width: '35%' },
  },
  {
    label: 'Type',
    key: 'is_diagnosis',
    style: { width: '10%' },
    format: (is_diagnosis) => {
      return (is_diagnosis ? 'Dx' : 'Px');
    }
  },
  {
    label: '# of Codes',
    key: 'code_count',
    style: { width: '8%' },
  },
 {
    label: 'Processing',
    key: 'processing_status',
    style: { width: '15%' },
  },   

  {
    label: 'Last Edit',
    key: 'last_edited',
    style: { width: '8%' },
    format: (date) => date ? moment(date).format('MM/DD/YYYY') : '',
  },
  {
    label: 'Last Edited by',
    key: 'edited_by_name',
    style: { width: '7%' },
  },
];

class CustomServiceLinesPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dialogOpen: false,
      selectedServiceLine: null,
      query: {
        attributes: [
          'id',
          'name',
          'last_edited',
          'last_edited_by',
          'is_diagnosis',
          'start_processing_date',
          'end_processing_date'
        ],
        include: [
          {
            association: 'edited_by_user',
            attributes: ['id', 'first_name', 'last_name'],
            required: false,
          },
          {
            association: 'codes',
            attributes: ['id', 'code_type', 'code','description'],
            required: false,
          },
        ],
        where: {
          client_id: props.clientId,
        },
        limit: 50,
        offset: 0,
        order: [['last_edited', 'DESC']],
      },
    };
  }

  componentDidMount() {
    this.fetchServiceLines();
  }

  componentDidUpdate(prevProps) {
    if (this.props.clientId !== prevProps.clientId) {
      const { query } = this.state;
      query.where.client_id = this.props.clientId;
      this.setState({ query }, this.fetchServiceLines);
    }
  }

  fetchServiceLines = () => {
    const { query } = this.state;
    query.where.client_id = this.props.clientId;
    this.props.dispatch(queryCustomServiceLines(query));
  };

  onQueryChange = (query) => {
    query.where.client_id = this.props.clientId;
    this.setState({ query }, this.fetchServiceLines);
  };

  onAddServiceLine = () => {
    this.setState({
      dialogOpen: true,
      selectedServiceLine: null,
    });
  };

  onRowClick = (row) => {
    this.setState({
      dialogOpen: true,
      selectedServiceLine: row,
    });
  };

  onCloseDialog = () => {
    this.setState({
      dialogOpen: false,
      selectedServiceLine: null,
    });
  };

  onSaveDialog = () => {
    this.setState({
      dialogOpen: false,
      selectedServiceLine: null,
    });
    this.fetchServiceLines();
    this.props.dispatch(listClaimsServicelines());
  };

  render() {
    const { classes, customServiceLines } = this.props;
    const { dialogOpen, selectedServiceLine, query } = this.state;

    console.log('customServiceLines:', customServiceLines); // Debug log

    // Transform data to include code_count, code_type, and created_by_name
    const transformedData = {
      ...customServiceLines,
      data: {
        ...customServiceLines.data,
        rows: customServiceLines.data?.rows?.map(row => {
          // Determine processing status
          let processingStatus = '';
          if (row.start_processing_date && !row.end_processing_date) {
            processingStatus = `Started ${DateUtil.formatFromNow(row.start_processing_date)}`;
          } 

          return {
            ...row,
            code_count: row.codes?.length || 0,
            code_type: row.code_type,
             processing_status: processingStatus,
            edited_by_name: row.edited_by_user
              ? `${row.edited_by_user.last_name}, ${row.edited_by_user.first_name}`
              : '',
          };
        }) || [],
      },
    };

    return (
      <>
        <title>Custom Service Lines</title>
        <div className={classes.container}>

          <DynamicTable
            header={
              <div className={classes.tableHeader}>
                <Typography
                  component='h2'
                  variant='h6'
                  color='primary'
                  gutterBottom
                >
                  Custom Service Lines
                </Typography>
                  <div className={classes.spacer} />
                <Button
                  color='primary'
                  size='small'
                  onClick={this.onAddServiceLine}
                >
                  <Icon className={classes.leftIcon}>add</Icon>
                  Add Custom Service Line
                </Button>
              </div>
            }
            columns={columns}
            query={query}
            data={transformedData}
            onRowClick={this.onRowClick}
            onQueryChange={this.onQueryChange}
            allowNew={false}
            showFilters={false}
          />
        </div>
        {dialogOpen && (
          <CustomServiceLineDialog
            open={dialogOpen}
            serviceLine={selectedServiceLine}
            clientId={this.props.clientId}
            userId={this.props.userId}
            onClose={this.onCloseDialog}
            onSave={this.onSaveDialog}
          />
        )}
      </>
    );
  }
}

CustomServiceLinesPage.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  clientId: PropTypes.number.isRequired,
  userId: PropTypes.number.isRequired,
  customServiceLines: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  console.log('state.claims:', state.claims); // Debug log
  console.log('custom_servicelines:', state.claims?.custom_servicelines); // Debug log
  
  return {
    clientId: state.app.selectedClient,
    userId: state.user.id,
    customServiceLines: state.claims?.custom_servicelines || {
      meta: { loadStatus: 'STATUS_NONE' },
      data: { rows: [], count: 0 }
    },
  };
}

const styled = withStyles(styles)(CustomServiceLinesPage);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as CustomServiceLinesPage };